/* Build-a-Box v2
 * Same rules & cart contract as v1:
 *  - box state lives in the DOM: .quantity[data-*] > .input_qty ; class .bottle-in-the-box when qty > 0
 *  - can't exceed pack size (opens the box drawer instead); box auto-opens when complete
 *  - switching to a SMALLER pack clears the box; upgrading keeps bottles
 *  - add to cart = 1 line item (pack variant) with properties {_boxID, _A: "handle | qty", ..., Contents}
 */
(() => {
  const root = document.getElementById('build-a-box-wrapper');
  if (!root) return;

  const $ = (sel, ctx = root) => ctx.querySelector(sel);
  const $$ = (sel, ctx = root) => Array.from(ctx.querySelectorAll(sel));

  const el = {
    packs: $$('input[name="size"]'),
    variantsJson: $('#build-a-box-variants [type="application/json"]'),
    grid: $('[data-grid]'),
    filters: $('[data-filters]'),
    filterMeta: $('[data-filter-meta]'),
    progress: $('#build-a-box-progress'),
    countMsgs: $$('.bottles-count-message'),
    hint: $('[data-hint]'),
    countBadge: $('[data-count-badge]'),
    barPrice: $(`#price-${root.dataset.section}`),
    pageAdd: $('#page-add-box-to-cart'),
    drawer: $('#build-a-box-modal'),
    drawerList: $('#build-a-box-contents'),
    drawerFooter: $('.modal__footer'),
    drawerCount: $('[data-drawer-count]'),
    drawerHint: $('[data-drawer-hint]'),
    drawerFill: $('[data-drawer-fill]'),
    drawerPrice: $('[data-drawer-price]'),
    steps: $$('.bab-step'),
    toast: $('[data-toast]'),
  };

  const variants = el.variantsJson ? JSON.parse(el.variantsJson.textContent) : [];
  const money = (cents) => {
    const v = (cents / 100).toFixed(2);
    return '$' + (v.endsWith('.00') ? v.slice(0, -3) : v);
  };

  /* ---------- state helpers (DOM is the source of truth) ---------- */
  const checkedPack = () => el.packs.find((p) => p.checked) || null;
  const size = () => { const p = checkedPack(); return p ? parseInt(p.dataset.size, 10) : 0; };
  const quantityEls = () => $$('.quantity');
  const total = () => quantityEls().reduce((sum, q) => sum + (parseInt(q.querySelector('.input_qty').value, 10) || 0), 0);
  const inBox = () => quantityEls().filter((q) => (parseInt(q.querySelector('.input_qty').value, 10) || 0) > 0);

  /* ---------- toast ---------- */
  let toastTimer;
  const toast = (msg) => {
    if (!el.toast) return;
    el.toast.textContent = msg;
    el.toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.toast.classList.remove('show'), 2200);
  };

  /* ---------- drawer ---------- */
  const openBox = () => { root.classList.add('is-box-open'); el.drawer?.setAttribute('aria-hidden', 'false'); };
  const closeBox = () => { root.classList.remove('is-box-open'); el.drawer?.setAttribute('aria-hidden', 'true'); };

  /* ---------- core mutation (same rules as v1) ---------- */
  const setQty = (quantityEl, next) => {
    const input = quantityEl.querySelector('.input_qty');
    input.value = Math.max(0, next);
    quantityEl.classList.toggle('bottle-in-the-box', next > 0);
    const badge = quantityEl.closest('.bab-card')?.querySelector('[data-inbox]');
    if (badge) badge.textContent = next;
  };

  const change = (quantityEl, delta) => {
    const s = size();
    if (!s) {
      toast('Pick a box size first');
      $('.bab-packs')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const cur = parseInt(quantityEl.querySelector('.input_qty').value, 10) || 0;
    if (delta > 0) {
      if (total() + 1 > s) { openBox(); toast(`Your ${s}-pack is full — upgrade or swap a bottle`); return; }
      setQty(quantityEl, cur + 1);
    } else {
      if (cur <= 0) return;
      setQty(quantityEl, cur - 1);
    }
    render();
    if (delta > 0 && total() === s) {
      toast('Box complete!');
      el.pageAdd?.classList.add('is-ready');
      setTimeout(openBox, 350);
    }
  };

  /* ---------- pack change (clear only on downgrade, like v1) ---------- */
  const onPackChange = () => {
    const s = size();
    if (s < total()) {
      quantityEls().forEach((q) => setQty(q, 0));
      toast('Smaller box — start fresh');
    }
    const v = variants.find((x) => String(x.id) === checkedPack()?.dataset.variant);
    if (v) history.replaceState({}, '', `${root.dataset.url}?variant=${v.id}`);
    render();
  };

  /* ---------- render everything from DOM state ---------- */
  const render = () => {
    const s = size(), t = total(), full = s > 0 && t === s;
    const pct = s ? Math.min(100, (t * 100) / s) : 0;
    const pack = checkedPack();
    const price = pack ? parseInt(pack.dataset.price, 10) : 0;
    const compare = pack ? parseInt(pack.dataset.compare, 10) : 0;

    if (el.progress) { el.progress.style.width = pct + '%'; el.progress.parentElement?.setAttribute('aria-valuenow', Math.round(pct)); }
    if (el.drawerFill) el.drawerFill.style.width = pct + '%';
    el.countMsgs.forEach((m) => (m.textContent = `${t} of ${s || '—'} bottles`));
    if (el.countBadge) el.countBadge.textContent = t;
    if (el.drawerCount) el.drawerCount.textContent = `${t}/${s || '—'}`;
    if (el.hint) el.hint.textContent = !s ? 'Pick a size to start' : full ? 'Ready to go' : `${s - t} more to fill your box`;
    if (el.drawerHint) el.drawerHint.textContent = !s ? 'Pick a size to start' : full ? 'Your box is complete' : t ? `${s - t} more to fill your box` : 'Your box is empty';
    if (el.barPrice) el.barPrice.innerHTML = pack ? `${compare > price ? `<s>${money(compare)}</s>` : ''}<span>${money(price)}</span>` : '';
    if (el.drawerPrice) el.drawerPrice.textContent = pack ? money(price) : '—';

    if (el.pageAdd) { el.pageAdd.disabled = !full; el.pageAdd.classList.toggle('disabled', !full); if (!full) el.pageAdd.classList.remove('is-ready'); }

    el.steps.forEach((st) => {
      const n = st.dataset.step;
      st.classList.toggle('is-done', (n === '1' && s > 0) || (n === '2' && full));
      st.classList.toggle('is-active', (n === '1' && !s) || (n === '2' && s > 0 && !full) || (n === '3' && full));
    });

    renderDrawerList();
    renderDrawerFooter(full, s);
  };

  const renderDrawerList = () => {
    if (!el.drawerList) return;
    const items = inBox();
    if (!items.length) {
      el.drawerList.innerHTML = '<li class="bab-drawer__empty">Your box is empty.<br>Add juices from the list.</li>';
      return;
    }
    el.drawerList.innerHTML = '';
    items.forEach((q) => {
      const qty = q.querySelector('.input_qty').value;
      const li = document.createElement('li');
      li.innerHTML = `
        <div class="image"><img src="${q.dataset.image}" alt=""></div>
        <div class="content"><div class="title"></div><div class="ingredients"></div></div>
        <div class="modal-quantity" data-variant="${q.dataset.variant}">
          <button type="button" class="modal-btn_qty minus" aria-label="Remove one">−</button>
          <input type="number" class="modal-input_qty" value="${qty}" readonly>
          <button type="button" class="modal-btn_qty plus" aria-label="Add one">+</button>
        </div>`;
      li.querySelector('.title').textContent = q.dataset.title;
      li.querySelector('.ingredients').textContent = q.dataset.ingredients || '';
      el.drawerList.appendChild(li);
    });
  };

  const renderDrawerFooter = (full, s) => {
    if (!el.drawerFooter) return;
    el.drawerFooter.innerHTML = '';
    const add = document.createElement('button');
    add.type = 'button'; add.className = 'add-box-to-cart'; add.textContent = 'Add Box to Cart'; add.disabled = !full;
    add.addEventListener('click', submitBox);
    el.drawerFooter.appendChild(add);

    if (full) {
      const idx = el.packs.findIndex((p) => p.checked);
      const next = el.packs[idx + 1];
      if (next) {
        const ns = parseInt(next.dataset.size, 10);
        const up = document.createElement('button');
        up.type = 'button'; up.className = 'upgrade-box';
        up.innerHTML = `Upgrade to ${ns}-pack <small>keep your bottles, add ${ns - s} more · ${money(parseInt(next.dataset.price, 10))}</small>`;
        up.addEventListener('click', () => { next.checked = true; next.dispatchEvent(new Event('change', { bubbles: true })); });
        el.drawerFooter.appendChild(up);
      }
    }
  };

  /* ---------- cart (contract unchanged) ---------- */
  const setButtons = (disabled, text) => {
    [$('.add-box-to-cart'), el.pageAdd].forEach((b) => { if (!b) return; b.disabled = disabled; b.classList.toggle('disabled', disabled); b.textContent = text; });
  };

  const submitBox = async (e) => {
    e?.preventDefault();
    const pack = checkedPack();
    if (!pack || total() !== size()) return;
    setButtons(true, 'Adding...');
    const properties = { _boxID: root.dataset.boxid || `_${Date.now()}` };
    let contents = '';
    inBox().forEach((q, i) => {
      const qty = parseInt(q.querySelector('.input_qty').value, 10);
      properties[`_${String.fromCharCode(65 + i)}`] = `${q.dataset.handle} | ${qty}`;
      contents += `${qty}x ${q.dataset.title} `;
    });
    properties.Contents = contents.trim();
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(pack.dataset.variant, 10), quantity: 1, properties }] }),
      });
      if (!res.ok) throw new Error(await res.text());
      window.location = '/cart';
    } catch (err) {
      console.error('Build-a-box add error:', err);
      setButtons(false, 'Add Box to Cart');
      toast('Something went wrong — please try again');
    }
  };

  /* ---------- filters ---------- */
  const filter = (cat) => {
    const cards = $$('.bab-card', el.grid);
    let shown = 0;
    cards.forEach((c) => {
      const ok = cat === 'all' || (c.dataset.cats || '').split(/\s+/).includes(cat);
      c.classList.toggle('is-hidden', !ok);
      if (ok) shown++;
    });
    if (el.filterMeta) el.filterMeta.textContent = `${shown} ${shown === 1 ? 'juice' : 'juices'}`;
  };

  /* ---------- events ---------- */
  el.packs.forEach((p) => p.addEventListener('change', onPackChange));

  el.grid?.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn_qty');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    change(btn.closest('.quantity'), btn.classList.contains('plus') ? 1 : -1);
  });

  el.drawerList?.addEventListener('click', (e) => {
    const btn = e.target.closest('.modal-btn_qty');
    if (!btn) return;
    const variant = btn.closest('.modal-quantity').dataset.variant;
    const q = $(`.quantity[data-variant="${variant}"]`);
    if (q) change(q, btn.classList.contains('plus') ? 1 : -1);
  });

  el.filters?.addEventListener('click', (e) => {
    const chip = e.target.closest('.bab-chip');
    if (!chip) return;
    $$('.bab-chip', el.filters).forEach((c) => c.classList.remove('is-on'));
    chip.classList.add('is-on');
    filter(chip.dataset.cat);
  });

  $$('[data-open-box]').forEach((b) => b.addEventListener('click', openBox));
  $$('[data-close-box]').forEach((b) => b.addEventListener('click', closeBox));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeBox(); });
  el.pageAdd?.addEventListener('click', submitBox);

  if (new URLSearchParams(location.search).get('show-box') === 'true') openBox();

  filter('all');
  render();
})();
